import React, { useEffect, useRef, useState, useMemo } from "react";
import { Col, Layout, Progress, Row } from "antd";
import { Content, Footer, Header } from "antd/es/layout/layout";
import bgLevelHighlight from "shared/assets/images/current-level-highlight.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  ArtistState,
  getUserArtistMilestones,
} from "explorer/reducers/artistas";
import { Goal } from "model/Goals";
import { motion } from "motion/react";
import { MilestoneRatioPeriod } from "shared/enums/MilestoneRatioPeriod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInstagram,
  faSpotify,
  faYoutube,
  IconDefinition,
} from "@fortawesome/free-brands-svg-icons";
import { Trans, useTranslation } from "react-i18next";
import { ReactComponent as AdvanceTourIcon } from "shared/assets/images/icon-advance-tour.svg";
import { ReactComponent as QuincyIcon } from "shared/assets/images/quincy-tour-icon.svg";
import { moneyFormatObject } from "shared/helpers/BRLFormat";
import { camelCase, range } from "lodash";
import { CashAdvanceAvailableEnum } from "shared/enums/CashAdvanceAvailable";
import {
  AdvanceState,
  getLoggedUserCashAdvanceAvailable,
} from "explorer/reducers/adiantamentos";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { fetchArtistsByGenre } from "shared/service/RadarService";
import { getGenrePercentageAtCurrentStage } from "shared/helpers/genre-artist-helpers";
import { isMobile } from "react-device-detect";
import Welcome from "./Welcome";
import "./TourVariantA.scss";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { TypingEffect } from "../Quincy/TypingEffect";

interface Step {
  show: boolean;
}

const LevelDetailsStep: React.FC<Step> = ({ show }) => {
  const { t, i18n } = useTranslation();
  const [showGlow, setShowGlow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [artistsByGenre, setArtistsByGenre] = useState<any[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { genre } = userArtist?.releaseFrequency || {};
  const { genres } = useSelector((state: any) => state.genres);
  const artistPercentageAtCurrentStage = getGenrePercentageAtCurrentStage(
    userArtist?.stage,
    artistsByGenre
  );
  const canShow = !loading;
  const localeString = i18n.language === "en" ? "en-US" : "pt-BR";

  useEffect(() => {
    if (!userArtist?.stage || loading) return;

    const container = scrollContainerRef.current;
    const activeEl = container?.querySelector(".level-item-current");
    if (!container || !activeEl) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const currentScroll = container.scrollLeft;

    const targetScroll =
      currentScroll +
      (activeRect.left - containerRect.left) -
      containerRect.width / 2 +
      activeRect.width / 2;

    const duration = 1500;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeInOut =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

      container.scrollLeft =
        currentScroll + (targetScroll - currentScroll) * easeInOut;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setShowGlow(true);
      }
    };

    requestAnimationFrame(animate);
  }, [userArtist?.stage, loading]);

  useEffect(() => {
    const getArtistByGenre = async () => {
      try {
        if (userArtist?.genresStrm?.length) {
          const response: any = await fetchArtistsByGenre(
            userArtist?.genresStrm[0]
          );
          setArtistsByGenre(response);
        }
      } catch (err) {
        console.error("Error to fetchArtistsByGenre on tour", err);
      } finally {
        setLoading(false);
      }
    };
    getArtistByGenre();
  }, [userArtist?.genresStrm]);

  return (
    <div
      className={`level-details-step ${isMobile ? "mobile" : ""}`}
      style={{
        opacity: canShow ? 1 : 0,
        visibility: canShow ? "visible" : "hidden",
        transition: "opacity 1s ease-in-out, visibility 1s ease-in-out",
      }}
    >
      <Row justify="center" className="default-pad">
        <p className="text-description">
          <Trans
            i18nKey="pages.Tour.VariantA.onlyOfTheArtists"
            values={{
              porcentage:
                artistPercentageAtCurrentStage.toLocaleString(localeString),
              genre: getI18nGenre(genre, i18n, genres),
            }}
            shouldUnescape={true}
          />
        </p>
      </Row>
      <div
        className="glow"
        style={{
          opacity: showGlow && canShow ? 1 : 0,
          visibility: showGlow && canShow ? "visible" : "hidden",
          transition: "opacity 1s ease-in-out, visibility 1s ease-in-out",
        }}
      />
      <div className="artist-level" ref={scrollContainerRef}>
        <div className="scroll-wrapper">
          <div className="level-item item-height-same">&nbsp;</div>
          <div className="level-item item-height-same">&nbsp;</div>
          <div className="level-item item-height-same">&nbsp;</div>
          {range(1, 11).map((stage) =>
            stage === userArtist?.stage ? (
              <div className="level-item-current" key={stage}>
                <div
                  className="hexagon item-height-same"
                  style={{
                    background: `url(${bgLevelHighlight}) no-repeat center`,
                  }}
                >
                  <p className="level-text-current">
                    <Trans
                      i18nKey="pages.Tour.artistLevel"
                      components={{
                        span: <span className="level-number-current"> </span>,
                      }}
                      values={{
                        level: stage,
                      }}
                    />
                  </p>
                </div>
                <div className="current-level">
                  <p className="level-name">
                    <Trans
                      i18nKey={`pages.Tour.level.${stage}.artist`}
                      components={{
                        span: <span className="color-light-lavender"> </span>,
                      }}
                      values={{
                        level: stage,
                      }}
                    />
                  </p>
                </div>
              </div>
            ) : (
              <div className="level-item item-height-same" key={stage}>
                {t(`pages.Tour.level.${stage}.name`)}
                <span className="level-number">{stage}</span>
              </div>
            )
          )}
          <div className="level-item item-height-same">&nbsp;</div>
          <div className="level-item item-height-same">&nbsp;</div>
          <div className="level-item item-height-same">&nbsp;</div>
        </div>
      </div>
    </div>
  );
};

const MetasStep: React.FC<Step> = ({ show }) => {
  const { t, i18n } = useTranslation();
  const localeString = i18n.language === "en" ? "en-US" : "pt-BR";
  const { milestones } = useSelector((state: ArtistState) => state.artists);

  useEffect(() => {
    console.log("MetasStep mounted");
  }, []);

  const renderMetaSection = (
    icon: IconDefinition,
    label: string,
    goals: Goal[] | undefined
  ) => {
    if (!goals) return null;

    return (
      <Row className="goal-container">
        <Col xs={24}>
          <p className="color-light-lavender goal-title">
            <FontAwesomeIcon icon={icon} /> {t(`pages.Tour.VariantA.${label}`)}
          </p>
        </Col>
        <Col xs={24}>{renderMetaItems(goals)}</Col>
      </Row>
    );
  };

  const renderMetaItems = (metasSelected: Goal[]) => {
    return (
      <>
        {metasSelected?.map((meta) => (
          <Col xs={24} className="goal-item" key={meta?.name}>
            <p className="goal-name color-light-lavender">
              {t(`pages.Metas.${camelCase(meta?.name)}`)}
            </p>
            <p>
              <span className="current-value">
                {Math.floor(meta?.currentValue ?? 0).toLocaleString(
                  localeString
                )}
              </span>{" "}
              /{meta?.milestone.toLocaleString(localeString)}
            </p>
            <Progress
              percent={Math.min(
                (meta?.currentValue / meta?.milestone) * 100,
                100
              )}
              strokeColor={{
                "0%": "#F680C8",
                "100%": "#4846CC",
              }}
              showInfo={false}
              size={{
                height: 6,
              }}
            />
          </Col>
        ))}
      </>
    );
  };
  return (
    <div className="goals-step">
      {renderMetaSection(faSpotify, "spotify", milestones?.spotify)}
      {renderMetaSection(faYoutube, "youtube", milestones?.youtube)}
      {renderMetaSection(faInstagram, "instagram", milestones?.instagram)}
    </div>
  );
};

const CashAdvanceOfferStep: React.FC<Step> = ({ show }) => {
  const { t } = useTranslation();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);

  useEffect(() => {
    console.log("CashAdvanceOfferStep mounted");
  }, []);

  return (
    <div className="cash-advance-offer-step">
      <p className="advance-amount-icon">
        <AdvanceTourIcon />
      </p>
      <p className="advance-amount">
        {moneyFormatObject(userArtist?.cashAdvanceEstimated)}
      </p>
      <p className="advance-amount-small">
        {t("pages.Tour.VariantA.available")}
      </p>
      <p className="advance-amount-disclaimer color-light-lavender">
        {t("pages.Tour.VariantA.valuesSubjectChange")}
      </p>
    </div>
  );
};

const QuincyStep: React.FC<Step> = ({ show }) => {
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const { t } = useTranslation();

  return (
    <Row className="quincy-step">
      <div className="quincy-card">
        <p className="quincy-card-header">
          <QuincyIcon /> {t("pages.Tour.VariantA.quincy")}
        </p>
        {show && (
          <TypingEffect
            fullText={t(`pages.Tour.VariantA.quincyText`, {
              name: userArtist?.name,
            })}
            className="quincy-card-description"
          />
        )}
      </div>
    </Row>
  );
};

const eventPageByStepName = {
  "stage-level": tagManagerEvent.Tour.ONBOARDING_STAGE_LEVEL,
  "goals": tagManagerEvent.Tour.ONBOARDING_GOALS,
  "funds": tagManagerEvent.Tour.ONBOARDING_FUNDS,
  "quincy": tagManagerEvent.Tour.ONBOARDING_QUINCY,
};

const TourA: React.FC = () => {
  const dispatch = useDispatch();
  const dispatchedStepsRef = useRef<Set<string>>(new Set());
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(0);
  const [finishedSteps, setFinishedSteps] = useState<boolean>(false);
  const { userArtist, loading, loadingMilestones } = useSelector(
    (state: ArtistState) => state.artists
  );
  const { cashAdvanceAvailable, loading: loadingAdvance } = useSelector(
    (state: AdvanceState) => state.advances
  );
  const currentPercentage = (currentIndex: number) =>
    (currentIndex + 1) * (100 / steps.length);

  const steps = useMemo(() => {
    const baseSteps = [
      {
        name: "stage-level",
        title: (
          <p>
            {t("pages.Tour.VariantA.congratulations")}
            <br />
            {t("pages.Tour.VariantA.youAreAtLevel", {
              level: userArtist?.stage,
            })}
          </p>
        ),
        content: (show: boolean) => <LevelDetailsStep show={show} />,
      },
    ];

    if (userArtist?.stage && userArtist?.stage < 10)
      baseSteps.push({
        name: "goals",
        title: <p>{t("pages.Tour.VariantA.yourPathIsClear")}</p>,
        content: (show: boolean) => <MetasStep show={show} />,
      });

    if (cashAdvanceAvailable === CashAdvanceAvailableEnum.Available) {
      baseSteps.push({
        name: "funds",
        title: <p>{t("pages.Tour.VariantA.getFunds")}</p>,
        content: (show: boolean) => <CashAdvanceOfferStep show={show} />,
      });
    }

    baseSteps.push({
      name: "quincy",
      title: <p>{t("pages.Tour.VariantA.talkToQuincy")}</p>,
      content: (show: boolean) => <QuincyStep show={show} />,
    });

    return baseSteps;
  }, [t, userArtist?.stage, cashAdvanceAvailable]);

  useEffect(() => {
    dispatch(getLoggedUserCashAdvanceAvailable(false));
  }, [dispatch]);

  useEffect(() => {
    if (userArtist?.id)
      dispatch(
        getUserArtistMilestones({
          artistId: userArtist!.id!,
          period: MilestoneRatioPeriod.TwelveMonths,
        })
      );
  }, [dispatch, userArtist]);

  useEffect(() => {
    const currentStep = steps[step];
    if (
      currentStep?.name &&
      !dispatchedStepsRef.current.has(currentStep.name)
    ) {
      dataLayerViewedEvent({
        ...eventPageByStepName[
          currentStep.name as keyof typeof eventPageByStepName
        ],
        ABTest: "FLOW_A",
      });
      dispatchedStepsRef.current.add(currentStep.name);
    }
  }, [step, steps]);

  if (loading || loadingAdvance || loadingMilestones)
    return <div className="tour-a"></div>;

  return (
    <div className="tour-a">
      <div className={`wrapper ${isMobile ? "mobile" : ""}`}>
        {steps.map((item, index) => (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: index === step ? 1 : 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            hidden={index !== step}
            style={{
              position: "absolute",
            }}
            className={`motion ${isMobile ? "mobile" : ""}`}
            key={index}
          >
            <Layout
              style={{
                position: "relative",
                display: "flex",
              }}
              className={`wrapper-step ${isMobile ? "mobile" : ""}`}
            >
              <Header className="header default-pad">
                <div className="progress-bars" style={{ width: "100%" }}>
                  <Progress
                    percent={currentPercentage(index)}
                    steps={steps.length}
                    showInfo={false}
                    style={{ width: "100%" }}
                  />
                </div>
                <Row className="header-title" justify="center">
                  {item.title}
                </Row>
              </Header>
              <Content className="content">
                {item.content(index === step)}
              </Content>
              <Footer className="footer default-pad">
                <button
                  className="botao-azul btn-forward"
                  onClick={() => {
                    setStep((prev) => prev + 1);
                    setFinishedSteps(index + 1 >= steps.length);
                  }}
                >
                  {t("pages.Tour.next")}
                </button>
              </Footer>
            </Layout>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: finishedSteps ? 1 : 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          hidden={!finishedSteps}
          style={{
            position: "absolute",
          }}
          className={`motion ${isMobile ? "mobile" : ""}`}
        >
          <Welcome show={finishedSteps} />
        </motion.div>
      </div>
    </div>
  );
};

export default TourA;
